import type { ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import type {
  ICreateRedistributionPricingPolicyPayload,
  IEditRedistributionPricingPolicyVariables,
  IGetOneRedistributionPricingPolicy,
  IQuoteErrorResponse,
  IRedistributionErrorResponse,
} from '~/purchase/types';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    ICreateRedistributionPricingPolicyPayload
  >;
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IGetOneRedistributionPricingPolicy>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      id: string;
    };
  };
  edit?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    IEditRedistributionPricingPolicyVariables
  >;
  remove?: UseMutationOptions<
    ISuccessApiResponse,
    IRedistributionErrorResponse,
    { policyGroupId: number }
  >;
};

export const useManageRedistributionPricingPolicies = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();

  const createMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-pricing-policies`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionPricingPolicies.base(),
    ],
    retry: false,
    ...options.create,
  });

  const editMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-pricing-policies/${variables.id}`,
        { method: 'PUT', body: JSON.stringify(variables.body) },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionPricingPolicies.base(),
    ],
    retry: false,
    ...options.edit,
  });

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.redistributionPricingPolicies.detail(
      options.get?.args.id as string,
    ),
    queryFn: () => {
      return callCoreApi<
        ISuccessApiResponse<IGetOneRedistributionPricingPolicy>
      >(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-pricing-policies/${options.get?.args.id}`,
      );
    },
    refetchOnWindowFocus: false,
    ...options.get,
    enabled: Boolean(options.get?.args.id) && options.get?.enabled,
  });
  const removeMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-pricing-policies/${variables.policyGroupId}`,
        { method: 'DELETE' },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionPricingPolicies.base(),
    ],
    ...options.remove,
  });

  return restrictApi(options, {
    create: createMutation,
    edit: editMutation,
    get: getQuery,
    remove: removeMutation,
  });
};
